import React, { useEffect, useState } from "react";
import {
  Popover,
  Button,
  Box,
  Typography,
  IconButton,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  TextField,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { IPincodeResponseDTO } from "../../interfaces/Orders";
import { getWarehousePincodes } from "../../services/Orders";
import { getDeliveryList } from "../../services/Delivery";

interface DeliveryFilterDialogProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  onApply: (filters: { pincodes: string[]; deliveryFrom: string; deliveryTo: string }, data: any[]) => void;
  selectedStatus: string;
  search: string;
}

const DeliveryFilterDialog = ({ open,anchorEl,onClose,onApply,selectedStatus,search }: DeliveryFilterDialogProps) => {
  const [activeFilter, setActiveFilter] = useState("Pincode");
  const [selectedPincodes, setSelectedPincodes] = useState<string[]>([]);
  const [deliveryFrom, setDeliveryFrom] = useState("");
  const [deliveryTo, setDeliveryTo] = useState("");
  const [pincodes, setPincodes] = useState<IPincodeResponseDTO[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        setLoading(true);
        const res = await getWarehousePincodes();
        setPincodes(Array.isArray(res) ? res : []);
      } catch {
        setPincodes([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);
  const handlePincodeToggle = (pin: string) => {
    setSelectedPincodes((prev) =>
      prev.includes(pin) ? prev.filter((p) => p !== pin) : [...prev, pin]
    );
  };

  const pincodeOptions = pincodes.map(p => p.pincode);

  const handleSelectAll = () => {
    if (activeFilter === "Pincode") {
      setSelectedPincodes(selectedPincodes.length === pincodeOptions.length ? [] : [...pincodeOptions]);
    }
  };

  const handleClearAll = () => {
    setSelectedPincodes([]);
    setDeliveryFrom("");
    setDeliveryTo("");
  };

  const [applying, setApplying] = useState(false);

  const handleApply = async () => {
    try {
      setApplying(true);
      const res = await getDeliveryList(
        selectedStatus,
        search || undefined,
        selectedPincodes.length ? selectedPincodes : undefined,
        deliveryFrom ? `${deliveryFrom}T00:00:00` : undefined,
        deliveryTo ? `${deliveryTo}T23:59:59` : undefined
      );
      onApply({ pincodes: selectedPincodes, deliveryFrom, deliveryTo }, Array.isArray(res) ? res : []);
    } catch {
      onApply({ pincodes: selectedPincodes, deliveryFrom, deliveryTo }, []);
    } finally {
      setApplying(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const renderRightContent = () => {
    if (activeFilter === "Pincode") {
      if (loading) {
        return (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        );
      }
      if (pincodeOptions.length === 0) {
        return <Typography variant="body2" color="grey.500" sx={{ py: 2, textAlign: "center" }}>No pincodes available</Typography>;
      }
      return (
        <Box sx={{ border: "1px solid", borderColor: "grey.300", borderRadius: "6px", p: 1}}>
          <FormControlLabel
            control={
              <Checkbox
                checked={selectedPincodes.length === pincodeOptions.length}
                indeterminate={selectedPincodes.length > 0 && selectedPincodes.length < pincodeOptions.length}
                onChange={handleSelectAll}
                sx={{ color: "grey.primary", "&.Mui-checked": { color: "primary.main" }, "&.MuiCheckbox-indeterminate": { color: "primary.main" } }}
              />
            }
            label={<Typography variant="body2">Select All</Typography>}
          />
          <Divider />
          <Box sx={{ maxHeight: "120px", overflowY: "auto", "&::-webkit-scrollbar": { width: "8px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" } }}>
            <FormGroup>
              {pincodeOptions.map((pin) => (
                <FormControlLabel
                  key={pin}
                  control={
                    <Checkbox
                      checked={selectedPincodes.includes(pin)}
                      onChange={() => handlePincodeToggle(pin)}
                      sx={{ color: "grey.primary", "&.Mui-checked": { color: "primary.main" } }}
                    />
                  }
                  label={<Typography variant="body2">{pin}</Typography>}
                />
              ))}
            </FormGroup>
          </Box>
        </Box>
      );
    } else if (activeFilter === "Delivery Date") {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>From</Typography>
            <TextField 
              fullWidth 
              size="small" 
              type="date" 
              value={deliveryFrom} 
              onChange={(e) => setDeliveryFrom(e.target.value)} 
              sx={{ 
                backgroundColor: "#F9FAFB",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  "& fieldset": {
                    borderColor: "rgba(74,85,101,0.15)",
                  },
                  "&:hover fieldset": { borderColor: "#000" },
                  "&.Mui-focused fieldset": { borderColor: "#000" },
                },
                "& .MuiInputBase-input": {
                  fontSize: "12px",
                  padding: "12px",
                },
              }} 
            />
          </Box>
          <Box>
            <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>To</Typography>
            <TextField 
              fullWidth 
              size="small" 
              type="date" 
              value={deliveryTo} 
              onChange={(e) => setDeliveryTo(e.target.value)} 
              sx={{ 
                backgroundColor: "#F9FAFB",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "6px",
                  "& fieldset": {
                    borderColor: "rgba(74,85,101,0.15)",
                  },
                  "&:hover fieldset": { borderColor: "#000" },
                  "&.Mui-focused fieldset": { borderColor: "#000" },
                },
                "& .MuiInputBase-input": {
                  fontSize: "12px",
                  padding: "12px",
                },
              }} 
            />
          </Box>
        </Box>
      );
    }
    return null;
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      transformOrigin={{ vertical: "top", horizontal: "right" }}
      PaperProps={{ sx: { borderRadius: "0.75rem", width: "450px", mt: 1 } }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 1.5}}>
        <Typography variant="subtitle1" fontWeight={600}>Filter</Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon fontSize="small" sx={{color:"#000"}} />
        </IconButton>
      </Box>
      
      <Divider />

      <Box sx={{ p: 0, display: "flex", maxHeight: "200px", backgroundColor: "background.default" }}>
        <Box sx={{ width: "40%", borderRight: "1px solid", borderColor: "grey.300", p: 1.5, backgroundColor: "background.default" }}>
          {["Pincode", "Delivery Date"].map((filter) => (
            <Box
              key={filter}
              onClick={() => setActiveFilter(filter)}
              sx={{
                p: 1,
                cursor: "pointer",
                borderRadius: "6px",
                mb: 0.5,
                backgroundColor: activeFilter === filter ? "white" : "transparent",
                color: activeFilter === filter ? "primary.main" : "text.primary",
                fontWeight: activeFilter === filter ? 600 : 400,
              }}
            >
              <Typography variant="body2">{filter}</Typography>
            </Box>
          ))}
        </Box>
        
        <Box sx={{ width: "60%", p: 1.5, backgroundColor: "white", overflowY: "auto", "&::-webkit-scrollbar": { width: "8px" }, "&::-webkit-scrollbar-track": { background: "transparent" }, "&::-webkit-scrollbar-thumb": { background: "#CBD5E1", borderRadius: "4px" } }}>
          {renderRightContent()}
        </Box>
      </Box>

      <Divider />

      <Box sx={{ px: 2, py:1, display: "flex", justifyContent: "space-between", backgroundColor: "background.default" }}>
        <Button onClick={handleClearAll} sx={{ textTransform: "none", color: "text.primary", "&:hover": { backgroundColor: "transparent" } }}>
          Clear all
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" onClick={handleClose} sx={{ borderRadius: "6px", textTransform: "none", borderColor: "grey.300", color: "text.primary", backgroundColor: "#fff", "&:hover": { backgroundColor: "#fff", borderColor: "grey.300" } }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleApply} disabled={applying} sx={{ borderRadius: "6px", textTransform: "none", backgroundColor: "primary.main", "&:hover": { backgroundColor: "primary.main" } }}>
            {applying ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "OK"}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
};
export default DeliveryFilterDialog;
